import React, { useState } from 'react';
import { Button, Checkbox, Form, Tooltip } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { useIntl } from 'umi';
import ContactAvatar from '@/components/Contacts/ContactAvatar';
import RoleInput from '@/components/Contacts/RoleInput';
import { ContactModel } from '@/typings/models/Contact';
import { getContactName } from '@/utils/contacts';
import { F } from '@/utils/intl';
import styles from './index.module.less';
import { stopEscape } from './keyboard';

type ContactRoleStepProps = {
  contact: ContactModel;
  /** Where the contact is linked today (another customer), so it's clear who's being added. */
  companyLabel?: string | null;
  roleSuggestions: string[];
  role: string;
  onRoleChange: (role: string) => void;
  busy?: boolean;
  /** Back to the search to pick someone else. */
  onChangeContact: () => void;
  onCancel: () => void;
  onSubmit: (options: { role: string; makePrimary: boolean }) => void;
};

const ContactRoleStep: React.FC<ContactRoleStepProps> = ({
  contact,
  companyLabel,
  roleSuggestions,
  role,
  onRoleChange,
  busy,
  onChangeContact,
  onCancel,
  onSubmit,
}) => {
  const intl = useIntl();
  const changeLabel = intl.formatMessage({
    id: 'component.contacts.changeContact',
    defaultMessage: 'Choose another contact',
  });
  const [makePrimary, setMakePrimary] = useState(false);
  const details = [contact.email || contact.phone, companyLabel].filter(Boolean).join(' · ');

  return (
    <div className={styles.panel} onKeyDown={stopEscape(onCancel)}>
      <div className={styles.pickedContact}>
        <ContactAvatar contact={contact} size="large" />
        <span className={styles.searchResultText}>
          <span className={styles.pickedName}>{getContactName(contact)}</span>
          {details && <span className={styles.searchResultDetails}>{details}</span>}
        </span>
        <Tooltip title={changeLabel}>
          <Button type="text" icon={<SwapOutlined />} aria-label={changeLabel} onClick={onChangeContact} />
        </Tooltip>
      </div>

      <Form layout="vertical" component="div">
        <Form.Item
          label={<F id="entity.role.singular" defaultMessage="Role" />}
          className={styles.compactItem}
        >
          <RoleInput value={role} onChange={onRoleChange} suggestions={roleSuggestions} autoFocus />
        </Form.Item>
      </Form>

      <div className={styles.panelFooter}>
        <Checkbox checked={makePrimary} onChange={(event) => setMakePrimary(event.target.checked)}>
          {intl.formatMessage({ id: 'component.contacts.primary', defaultMessage: 'Primary contact' })}
        </Checkbox>
        <span className={styles.panelActions}>
          <Button onClick={onCancel} disabled={busy}>
            <F id="common.actions.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="primary" loading={busy} onClick={() => onSubmit({ role: role.trim(), makePrimary })}>
            <F id="common.actions.add" defaultMessage="Add" />
          </Button>
        </span>
      </div>
    </div>
  );
};

export default ContactRoleStep;
